import axios from 'axios';
import _ from 'lodash'
import { ActionTree } from 'vuex';
import moment from 'moment';
import { RootState } from '@/store/types';
import {
  TutorsState,
  Tutor,
  TutorConditions,
  TravelExpense,
  Group,
  Contract,
  AdditionalContract,
} from '@/store/tutors/types';
import { getPagerFromApiResponse } from '@/store/utils';
import { Sort } from '@/store/sort.types';

/**
 * APIレスポンスのtutor情報を変換
 * @param tutorByApi APIからレスポンスされたtutor
 * @returns {Tutor} チューター
 */
export const convertTutorFromApi = (tutorByApi: any, getters: any): Tutor => {
  let name = ''
  let firstName = ''
  let middleName = null
  let lastName = ''
  if (_.has(tutorByApi, 'tutorName')) {
    name = tutorByApi.tutorName
    const names = name.split(' ')
    firstName = String(_.first(names))
    lastName = String(_.last(names))
    if (names.length > 2) {
      middleName = String(names[1])
    }
  } else {
    firstName = tutorByApi.firstName
    middleName = tutorByApi.middleName
    lastName = tutorByApi.lastName
    name = _.compact([firstName, middleName, lastName]).join(' ')
  }

  return {
    id: tutorByApi.tutorId,
    name,
    authority: tutorByApi.authority,
    gender: tutorByApi.gender,
    position: {
      code: tutorByApi.positionCode,
      name: tutorByApi.position,
    },
    address: tutorByApi.addressEnglish,
    addressJapanese: tutorByApi.address,
    birthday: tutorByApi.birthday && moment(tutorByApi.birthday, 'YYYYMMDD'),
    email1: tutorByApi.email1,
    email2: tutorByApi.email2,
    emergencyContact1: tutorByApi.emergencyContact1,
    emergencyContact2: tutorByApi.emergencyContact2,
    firstName,
    middleName,
    lastName,
    hireDate: tutorByApi.hireDate && moment(tutorByApi.hireDate, 'YYYYMMDD'),
    leaveDate: tutorByApi.leaveDate && moment(tutorByApi.leaveDate, 'YYYYMMDD'),
    nationality: {
      code: tutorByApi.nationalityCode,
      name: tutorByApi.nationalityEnglish,
      nameJapanese: tutorByApi.nationality,
    },
    phone1: tutorByApi.phone1,
    userImage: tutorByApi.userImage,
    academicBackground: tutorByApi.academicBackground,
    lmsToken: tutorByApi.lmsToken,
    lmsUserId: tutorByApi.lmsUserId,
    note: tutorByApi.note,
    teachingExperience: tutorByApi.teachingExperience,
    totalExperience: tutorByApi.totalExperience,
    waitingPriority: tutorByApi.waitingPriority || false,
    travelExpenses: tutorByApi.transportationExpenses?.map(
      (transportationExpense: any): TravelExpense => {
        return {
          campus: getters.findCampusByCode(transportationExpense.campusCode),
          fee: transportationExpense.fee,
          route: transportationExpense.route,
          commutingTime: transportationExpense.commutingTime,
          validityDateFrom: moment(transportationExpense.startValidityPeriod, 'YYYYMMDD'),
          validityDateTo: moment(transportationExpense.endValidityPeriod, 'YYYYMMDD'),
          nearestStation: transportationExpense.nearestStation,
          transportationExpenseId: transportationExpense.transportationExpenseId,
        }
      }
    ),
    groups: [], // Groupは別途取得
    contracts: _.map(tutorByApi.contracts, (c): Contract => ({
      contractNumber: c.number,
      from: moment(c.from, 'YYYYMMDD'),
      to: moment(c.to, 'YYYYMMDD'),
      position: {
        code: c.positionCode,
        name: c.position,
      },
      isFixedTerm: c.isFixedTerm,
      reviewSheet: c.reviewSheet,
      salary: {
        fixed: c.fixedSalary,
        hourly: c.hourlyWage,
        teSubstituteLesson: c.teSubstituteLesson,
        wteSubstituteLesson: c.wteSubstituteLesson,
        wteOneFrameAbsenteeism: c.wteOneframeAbsenteeism,
        substituteForTenMinutes: c.tenMinSub,
      },
      numberOf: {
        lessonsForWeek: c.lessonWeek,
        lessonsForMonth: c.lessonMonth,
        waitingsForWeek: c.waiting,
        lastWaitingsForWeek: c.lastWaiting,
      },
      vacation: {
        plannedGrants: c.plannedGrantDays,
        remainingGrants: c.plannedRemainingDays,
        plannedHolidays: c.freeHolidays,
        remainingHolidays: c.freeRemainingHolidays,
      },
      passport: {
        number: c.passport.number,
        status: c.passport.residenceStatus,
        from: moment(c.passport.from, 'YYYYMMDD'),
        to: moment(c.passport.to, 'YYYYMMDD'),
        residence: {
          card: c.passport.residenceCard,
          from: moment(c.passport.residenceFrom, 'YYYYMMDD'),
          to: moment(c.passport.residenceTo, 'YYYYMMDD'),
        },
      },
      additionalContracts: _.map(c.additionals, (a): AdditionalContract => ({
        hourly: a.hourlyWage,
        fixed: a.fixedSalary,
        workQuantity: a.workQuantity,
      })).filter((a) => a.fixed !== null && a.hourly !== null),
    })),
  }
}

const actions: ActionTree<TutorsState, RootState> = {
  /**
   * チューターの一覧を取得
   * @param {{
   *  page: number, // ページ番号
   *  conditions: TutorConditions, // 検索条件
   *  sortBy: Sort, // ソート条件
   * }}
   */
  fetchTutors: async (
    { commit, dispatch, getters },
    { page = 1, conditions, sortBy } : {
      conditions?: TutorConditions,
      page?: number,
      sortBy?: Sort
    },
  ) => {
    const { headers, data } = await axios.get('/tutors', {
      params: {
        ..._.pickBy({
          tutor: conditions?.tutorNameOrId,
          positionCode: conditions?.positions?.map(({ code }) => code).join(','),
          includeTerminationOfService: conditions?.isIncludeTerminationOfService ? 1 : 0,
          gender: conditions?.genderCodes?.join(','),
          nationalityCode: conditions?.nationality?.code,
          authority: conditions?.authorities?.join(','),
          sortBy: _.values(sortBy).join(':'),
        }),
        page,
      },
    })
    await dispatch('fetchMasterCampuses')

    const tutors = _.map(data, (tutor) => convertTutorFromApi(tutor, getters));
    commit('fetchedTutors', {
      pager: getPagerFromApiResponse(page, headers),
      tutors,
      sortBy,
    });

    return tutors
  },

  /**
   * 対象のチューターを一件取得
   * @param {{
   *  tutorId: string, チューターID
   * }}
   */
  getTutor: async (
    { commit, getters, dispatch },
    { tutorId } : { tutorId: string },
  ) => {
    await dispatch('fetchMasterCampuses')
    const { data } = await axios.get(`/tutors/${tutorId}`)
    commit('gotTutor', convertTutorFromApi(data, getters))
  },

  /**
   * チューターが担当するグループを取得
   * @param {{
   *  tutorId: string, // チューターID
   *  fiscalYear: number, // 年度
   * }}
   */
  fetchTutorGroups: (
    { commit, dispatch, getters },
    { tutorId, fiscalYear } : { tutorId: string, fiscalYear: number },
  ) => axios.get(`/tutors/${tutorId}/groups`, {
    params: {
      fiscalYear,
    },
  })
    .then(async (response) => {
      commit('fetchedTutorGroups', {
        tutorId,
        groups: _.map(response.data, (data): Group => ({
          course: {
            name: data.courseName,
          },
          semester: {
            name: data.semester,
          },
          weekday: data.dayOfWeek,
          period: data.period,
          lessonId: data.lessonId,
        })),
      });
    }),

  /**
   * チューターの画像を取得
   * @param {{
   *  tutorId: string, // チューターID
   *  userImage: string, // チューター画像
  *  }}
   */
  fetchTutorImage: (
    { commit },
    { tutorId, userImage } : { tutorId: string, userImage: string },
  ) => {
    const data = new FormData();
    data.append('userImage', userImage);
    const headers = { 'content-type': 'multipart/form-data' };
    return axios.post(`/tutors/${tutorId}/userImage`, data, { headers });
  },

  /**
   * チューター登録
   * @param {Tutor} tutor チューター情報
   */
  registerTutor: ({ commit }, tutor: Tutor) => axios.post('/tutors', _.pickBy({
    tutorId: tutor.id,
    lastName: tutor.lastName,
    firstName: tutor.firstName,
    middleName: tutor.middleName,
    phone1: tutor.phone1,
    email1: tutor.email1,
    email2: tutor.email2,
    address: tutor.addressJapanese,
    addressEnglish: tutor.address,
    emergencyContact1: tutor.emergencyContact1,
    emergencyContact2: tutor.emergencyContact2,
    nationalityCode: tutor.nationality?.code,
    birthday: tutor.birthday?.format('YYYYMMDD'),
    gender: tutor.gender,
    positionCode: tutor.position?.code,
    hireDate: tutor.hireDate?.format('YYYYMMDD'),
    leaveDate: tutor.leaveDate?.format('YYYYMMDD'),
    academicBackground: tutor.academicBackground,
    teachingExperience: tutor.teachingExperience,
    totalExperience: tutor.totalExperience,
    waitingPriority: tutor.waitingPriority,
    lmsUserId: tutor.lmsUserId,
    lmsToken: tutor.lmsToken,
    note: tutor.note,
  })),

  /**
   * チューター登録
   * @param {{
   *  id: string, // チューターID
   *  tutor: Tutor, // チューター情報
   * }}
   */
  changeTutorProfile: (
    { commit },
    { id, tutor }: { id: string, tutor: Tutor },
  ) => axios.put(`/tutors/${id}`, {
    ...tutor,
    nationalityCode: tutor.nationality?.code,
    positionCode: tutor.position?.code,
    hireDate: tutor.hireDate?.format('YYYYMMDD'),
    birthday: tutor.birthday?.format('YYYYMMDD'),
    leaveDate: tutor.leaveDate?.format('YYYYMMDD'),
    address: tutor.addressJapanese,
    addressEnglish: tutor.address,
  }),

  /**
   * チューター一括登録（CSVインポート）
   * @param {File} csvFile チューターの一括登録用CSV
   */
  importTutorsByCsv: (context, csvFile: File) => {
    const formData = new FormData();
    formData.append('csv', csvFile);
    return axios.post('/tutors/bulkUpdate', formData);
  },

  /**
   * 交通費登録
   * @param {{
   *  tutorId: string, // チューターID
   *  postTraffic: TravelExpense, // 交通費
   * }}
   */
  registerTraffic: (
    { commit },
    { tutorId, postTraffic }: { tutorId: string, postTraffic: TravelExpense },
  ) => {
    // 日付再変換
    const { validityDateFrom, validityDateTo, campus } = postTraffic;
    return axios.post(
      `/tutors/${tutorId}/transportationExpenses`,
      {
        ...postTraffic,
        ...{
          campusCode: campus.code,
          startValidityPeriod: validityDateFrom && moment(validityDateFrom).format('YYYYMMDD'),
          endValidityPeriod: validityDateTo && moment(validityDateTo).format('YYYYMMDD'),
        },
      },
    );
  },

  /**
   * 交通費削除
   * @param {{
   *  tutorId: string, // チューターID
   *  deleteTraffic: TravelExpense, // 交通費
   * }}
   */
  deleteTraffic: (
    { commit },
    { tutorId, deleteTraffic }: { tutorId: string, deleteTraffic: TravelExpense },
  ) => axios.delete(`/tutors/${tutorId}/transportationExpenses/${deleteTraffic.transportationExpenseId}`),

  /**
   * 交通費編集
   * @param {{
   *  tutorId: string, // チューターID
   *  changeTraffic: TravelExpense, // 交通費
   * }}
   */
  changeTraffic: (
    { commit },
    { tutorId, changeTraffic }: { tutorId: string, changeTraffic: TravelExpense },
  ) => {
    // 日付再変換
    const { validityDateFrom, validityDateTo, campus } = changeTraffic;
    return axios.put(
      `/tutors/${tutorId}/transportationExpenses/${changeTraffic.transportationExpenseId}`,
      {
        ...changeTraffic,
        ...{
          campusCode: campus.code,
          startValidityPeriod: validityDateFrom && moment(validityDateFrom).format('YYYYMMDD'),
          endValidityPeriod: validityDateTo && moment(validityDateTo).format('YYYYMMDD'),
        },
      },
    );
  },

  /**
   * 契約情報出力
   * @param {{
   *  selectedTutorIds: string[], // 選択されたチューターIDの配列
   *  selectedYear: string, // 選択された年度
   * }}
   */
  exportTutorContractByCsv: async (
    context,
    { selectedTutorIds, selectedYear }: { selectedTutorIds: string[], selectedYear: string },
  ) => {
    const { data, headers } = await axios.get('/contracts/csv', {
      params: {
        ..._.pickBy({
          tutorIds: selectedTutorIds.join(','),
          fiscalYear: selectedYear,
        }),
      },
    });

    const filename = _.chain(headers)
      .get('content-disposition')
      .split(';')
      .reduce((carry, directive) => {
        const [empty, name] = directive.trim().split('filename=');
        return name ?? carry;
      }, {})
      .value();

    return {
      content: data,
      filename,
    }
  },
};

export default actions;
